import { useCallback } from 'react';
import { useCopyToClipboard } from '@shared/utils/hooks/useCopyToClipboard';
import { useToast } from '@withjoy/joykit';

interface CopyLinkConfig<TContext> {
  successMessage: string;
  onSuccess?: (generatedLink: string) => void;
  onError?: (error: Error, context?: TContext) => void;
  context?: TContext;
}

/**
 * Hook that provides copy link success and error handlers
 * @param config - Configuration object with toast, translation, success/error callbacks, and optional context
 * @returns Object containing the handleCopyShareableLink function
 */
export const useCopyLinkHandlers = <TContext>(config: CopyLinkConfig<TContext>) => {
  const copyToClipboard = useCopyToClipboard();
  const { toast } = useToast();

  const onCopySuccess = useCallback(
    (generatedLink: string) => {
      toast(config.successMessage);
      config.onSuccess?.(generatedLink);
    },
    [config, toast],
  );

  const onCopyError = useCallback(
    (error: Error) => {
      config.onError?.(error, config.context);
    },
    [config],
  );

  const handleCopyShareableLink = useCallback(
    (link: string) => {
      try {
        if (link) {
          copyToClipboard(link);
          onCopySuccess?.(link);
        }
      } catch (error) {
        onCopyError?.(error as Error);
      }
    },
    [copyToClipboard, onCopySuccess, onCopyError],
  );

  return { handleCopyShareableLink };
};
